// SEQ-145 Amendment 3 (Josh-reported): claims_hire_billing.claim_report_id is
// ON DELETE NO ACTION (every other claims_reports reference cascades), so a
// claim with hire rows cannot be deleted until those rows are released. The
// delete flow calls this FIRST; if it throws, the delete must not be attempted.
//
// Fail-safe ordering (review finding, 24 Jul): capture the unpaid ids, release
// EVERY referencing row, and only then reset the captured ids to 'pending'.
// Resetting status first would create rows that are 'pending' while still
// attached to the live report - isHireRowClaimable accepts 'pending' before it
// checks the claim, so a failure between the steps would leave them claimable
// while attached, the exact double-billing shape Amendment 1 fixed. With this
// order every partial-failure state is inert ('included' + null
// claim_report_id is rejected by the predicate).
//
// On the status reset itself: the plan's "leave billing_status untouched"
// parenthetical would strand unpaid rows as 'included' with no claim, which
// the predicate rejects; resetting to 'pending' is what its stated intent
// ("correctly re-claimable") and the Amendment 1 restore precedent require.
// Paid rows keep their status: the paid flag alone blocks them. The unpaid
// filter is null-safe on purpose (.eq("paid", false) would miss paid = null).

import { readReclaimOrigins, hireTupleKey } from "./hireClaimability";

// SEQ-218: an unpaid month that this claim RE-CLAIMED from an earlier claim goes
// back to that claim when this one is deleted, instead of being released to
// unattached pending. Released, it would drop out of hire claimed to date (A)
// on every later claim, although the earlier claim still asked for it. The move
// comes first and only to a claim that still exists and is strictly earlier;
// everything else is released exactly as before.

interface SupabaseLikeClient {
  // Matches the supabase-js query-builder surface this module uses; typed
  // loosely so tests can drive it with a recording fake.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  from(table: string): any;
}

/**
 * Releases every claims_hire_billing row attached to the report so the
 * claims_reports delete can proceed. Returns the number of rows released.
 * Throws the DB error untouched on failure (the caller surfaces it verbatim
 * and must not attempt the delete).
 */
export async function releaseHireRowsForReport(
  client: SupabaseLikeClient,
  reportId: string,
): Promise<number> {
  const unpaidCapture = await client
    .from("claims_hire_billing")
    .select("id, hire_register_id, billing_month, billing_year")
    .eq("claim_report_id", reportId)
    .or("paid.is.null,paid.eq.false");
  if (unpaidCapture.error) throw unpaidCapture.error;
  const unpaidRows: {
    id: string;
    hire_register_id?: string | null;
    billing_month?: number | null;
    billing_year?: number | null;
  }[] = unpaidCapture.data || [];
  let unpaidIds: string[] = unpaidRows.map((row) => row.id);

  // Move re-claimed rows back to the claim they came from, before anything is
  // released. A failure here throws before any release, so no row is left in
  // a worse state than it started in.
  if (unpaidRows.length > 0) {
    const moved = await moveReclaimedRowsBack(client, reportId, unpaidRows);
    if (moved.size > 0) unpaidIds = unpaidIds.filter((id) => !moved.has(id));
  }

  const release = await client
    .from("claims_hire_billing")
    .update({ claim_report_id: null, included_in_claim: false })
    .eq("claim_report_id", reportId)
    .select("id");
  if (release.error) throw release.error;

  if (unpaidIds.length > 0) {
    const statusReset = await client
      .from("claims_hire_billing")
      .update({ billing_status: "pending" })
      .in("id", unpaidIds);
    if (statusReset.error) throw statusReset.error;
  }

  return (release.data || []).length;
}

async function moveReclaimedRowsBack(
  client: SupabaseLikeClient,
  reportId: string,
  unpaidRows: readonly {
    id: string;
    hire_register_id?: string | null;
    billing_month?: number | null;
    billing_year?: number | null;
  }[],
): Promise<Set<string>> {
  const moved = new Set<string>();
  const report = await client
    .from("claims_reports")
    .select("project_id, claim_year, claim_month, claimed_set")
    .eq("id", reportId)
    .maybeSingle();
  if (report.error) throw report.error;
  const info = report.data as {
    project_id?: string;
    claim_year?: number;
    claim_month?: number;
    claimed_set?: unknown;
  } | null;
  if (!info?.project_id) return moved;
  // Cheap exit: nothing frozen as a re-claim means nothing to look up.
  if (readReclaimOrigins(info.claimed_set).size === 0) return moved;

  const siblings = await client
    .from("claims_reports")
    .select("id, claim_year, claim_month")
    .eq("project_id", info.project_id);
  if (siblings.error) throw siblings.error;
  const origins = readReclaimOrigins(info.claimed_set, {
    reports: siblings.data || [],
    claimYear: Number(info.claim_year),
    claimMonth: Number(info.claim_month),
  });
  if (origins.size === 0) return moved;

  for (const row of unpaidRows) {
    if (!row.hire_register_id) continue;
    const origin = origins.get(
      hireTupleKey(
        row.hire_register_id,
        Number(row.billing_month),
        Number(row.billing_year),
      ),
    );
    if (!origin) continue;
    const move = await client
      .from("claims_hire_billing")
      .update({
        claim_report_id: origin,
        included_in_claim: true,
        billing_status: "included",
      })
      .eq("id", row.id)
      // Only while it is still this claim's and unpaid.
      .eq("claim_report_id", reportId)
      .or("paid.is.null,paid.eq.false");
    if (move.error) throw move.error;
    moved.add(row.id);
  }
  return moved;
}
